// ABOUTME: Continuous 16kHz mono PCM16 mic capture for live conversation mode
// (#2609 Part 3). Separate from useVoiceRecorder (Part 1): that hook records
// a whole containerized clip (webm/opus) then uploads once; this streams raw
// PCM frames the whole session via an AudioWorklet, which ElevenLabs'
// realtime STT needs and a containerized recorder can't produce.

import { useCallback, useRef, useState } from 'react'

const WORKLET_URL = '/audio/live-voice-processor.worklet.js'
const PROCESSOR_NAME = 'live-voice-processor'

export type LiveVoiceCaptureStatus =
  | 'idle'
  | 'requesting_permission'
  | 'capturing'
  | 'error'
  | 'unsupported'

export interface UseLiveVoiceCaptureOptions {
  /** One 16kHz mono PCM16 frame (~100ms), ready to send over the wire. */
  onAudioFrame: (pcm16: ArrayBuffer) => void
  /** RMS amplitude of the same frame, 0-1ish — drives local barge-in VAD. */
  onLevel?: (rms: number) => void
  onError?: (message: string) => void
}

interface WindowWithWebkitAudioContext extends Window {
  webkitAudioContext?: typeof AudioContext
}

function getAudioContextCtor(): typeof AudioContext | undefined {
  if (typeof window === 'undefined') return undefined
  return window.AudioContext ?? (window as WindowWithWebkitAudioContext).webkitAudioContext
}

function isSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    !!getAudioContextCtor()
  )
}

export function useLiveVoiceCapture(options: UseLiveVoiceCaptureOptions) {
  const { onAudioFrame, onLevel, onError } = options
  const [status, setStatus] = useState<LiveVoiceCaptureStatus>(() =>
    isSupported() ? 'idle' : 'unsupported'
  )

  const contextRef = useRef<AudioContext | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const nodeRef = useRef<AudioWorkletNode | null>(null)

  const stop = useCallback(() => {
    nodeRef.current?.disconnect()
    nodeRef.current = null
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    if (contextRef.current && contextRef.current.state !== 'closed') {
      void contextRef.current.close()
    }
    contextRef.current = null
    setStatus((prev) => (prev === 'error' || prev === 'unsupported' ? prev : 'idle'))
  }, [])

  const start = useCallback(async () => {
    if (!isSupported()) {
      setStatus('unsupported')
      onError?.('Live conversation mode needs microphone support this browser lacks.')
      return
    }
    setStatus('requesting_permission')

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      setStatus('error')
      onError?.('Microphone access denied.')
      return
    }

    try {
      const AudioContextCtor = getAudioContextCtor()
      if (!AudioContextCtor) throw new Error('AudioContext is not supported')
      const context = new AudioContextCtor()
      await context.audioWorklet.addModule(WORKLET_URL)

      const source = context.createMediaStreamSource(stream)
      const node = new AudioWorkletNode(context, PROCESSOR_NAME)
      node.port.onmessage = (event: MessageEvent) => {
        const data = event.data as { type: string; pcm16?: ArrayBuffer; rms?: number }
        if (data.type === 'audio' && data.pcm16) {
          onAudioFrame(data.pcm16)
          if (typeof data.rms === 'number') onLevel?.(data.rms)
        }
      }
      source.connect(node)

      contextRef.current = context
      streamRef.current = stream
      nodeRef.current = node
      setStatus('capturing')
    } catch (err) {
      stream.getTracks().forEach((track) => track.stop())
      setStatus('error')
      onError?.(err instanceof Error ? err.message : 'Could not start microphone capture.')
    }
  }, [onAudioFrame, onLevel, onError])

  return {
    status,
    isCapturing: status === 'capturing',
    isSupported: status !== 'unsupported',
    start,
    stop,
  }
}

export default useLiveVoiceCapture
